import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type CellStyle,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type RowClickedEvent,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { Search, RefreshCw, Flag, Check, Columns3 } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import { SELECTION_COLUMN_DEF, emptyFormatter } from '../../lib/gridUtils';
import { reportDatasourceFailure } from '../../lib/agGridErrorTracker';
import ExportButton from '../../components/ui/ExportButton';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { Toast, type ToastState } from '../../components/ui/Toast';
import { extractEnvelope } from '../../lib/extractEnvelope';

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Flag category styling ───────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  unknown_brand: 'Unknown Brand',
  no_market_price: 'No Match',
  missing_price: 'Missing Price',
  missing_demand: 'Missing Demand',
  missing_identifier: 'Missing ID',
  no_amazon_listing: 'No Amazon Listing',
  data_quality: 'Data Quality',
  conflicting_matches: 'Conflict',
};

const CATEGORY_COLORS: Record<string, string> = {
  unknown_brand: 'bg-warning/15 text-warning',
  no_market_price: 'bg-danger/15 text-danger',
  missing_price: 'bg-danger/15 text-danger',
  missing_demand: 'bg-warning/15 text-warning',
  missing_identifier: 'bg-surface-4 text-ink-muted',
  no_amazon_listing: 'bg-info/15 text-info',
  data_quality: 'bg-surface-4 text-ink-muted',
  conflicting_matches: 'bg-warning/15 text-warning',
};

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-danger/15 text-danger',
  resolved: 'bg-success/15 text-success',
  dismissed: 'bg-surface-4 text-ink-muted',
};

// ── Hold-pile disposition styling (W4b-β1) ──────────────────────────────────
// archive / named_backlog / chase_upcs are written here (bulk, direct PostgREST);
// 'retry' has no β1 writer (its RPC + the resolver re-arm is W4b-β2) — the entry is
// defensive value-before-use so it renders the moment β2 starts writing it.
const DISPOSITION_LABELS: Record<string, string> = {
  retry: 'Retry',
  archive: 'Archived',
  named_backlog: 'Backlog',
  chase_upcs: 'Chase UPCs',
};

const DISPOSITION_COLORS: Record<string, string> = {
  retry: 'bg-accent-muted text-accent',
  archive: 'bg-surface-4 text-ink-muted',
  named_backlog: 'bg-info/15 text-info',
  chase_upcs: 'bg-warning/15 text-warning',
};

interface CategoryOption {
  id: string;
  label: string;
}

const CATEGORY_OPTIONS: CategoryOption[] = [
  { id: 'all', label: 'All Categories' },
  { id: 'unknown_brand', label: 'Unknown Brand' },
  { id: 'no_market_price', label: 'No Market Price' },
  { id: 'missing_price', label: 'Missing Price' },
  { id: 'missing_demand', label: 'Missing Demand' },
  { id: 'missing_identifier', label: 'Missing Identifier' },
  { id: 'no_amazon_listing', label: 'No Amazon Listing' },
  // W4b-α — `data_quality` dropped as a quick-filter OPTION (scan_product_flags never emits it — an
  // orphan). The defensive CATEGORY_LABELS / CATEGORY_COLORS map entries stay (harmless display fallback).
];

interface ChoosableColumn {
  colId: string;
  label: string;
}

const CHOOSABLE_COLUMNS_FLAGGED: ChoosableColumn[] = [
  { colId: 'title', label: 'Title' },
  { colId: 'brand', label: 'Brand' },
  { colId: 'flag_category', label: 'Category' },
  { colId: 'flag_reason', label: 'Reason' },
  { colId: 'status', label: 'Status' },
  { colId: 'flag_disposition', label: 'Disposition' },
  { colId: '_actions', label: 'Actions' },
];

type FlagStatus = 'open' | 'resolved' | 'dismissed';
type StatusFilter = FlagStatus | 'all';

// Row returned by product_flags + join on products.
interface FlagRow {
  id: string;
  status: FlagStatus;
  flag_category: string | null;
  flag_reason: string | null;
  flag_disposition: string | null;
  created_at: string;
  isg_sku: string | null;
  products?: {
    id?: string;
    isg_sku?: string | null;
    title?: string | null;
    brand?: string | null;
    image_url?: string | null;
  } | null;
}

// ── Flagged page ────────────────────────────────────────────────────────────

export default function Flagged(): JSX.Element {
  const { user, isViewer, isDistRep } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const gridRef = useRef<AgGridReact<FlagRow> | null>(null);
  const gridApiRef = useRef<GridApi<FlagRow> | null>(null);

  const [searchText, setSearchText] = useState<string>(searchParams.get('sku') || '');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [scanning, setScanning] = useState<boolean>(false);
  const [dismissConfirm, setDismissConfirm] = useState<string | null>(null);
  const [colChooserOpen, setColChooserOpen] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<FlagRow[]>([]);
  // KA1-F2 (FUNCTIONAL Type-1) — the exact flagged-row count for the toolbar indicator.
  const [totalCount, setTotalCount] = useState(0);
  const [bulkBusy, setBulkBusy] = useState<boolean>(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  // Column visibility — persisted in localStorage
  const [hiddenCols, setHiddenCols] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('arbi_flagged_columns');
      return saved ? (JSON.parse(saved) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });

  const toggleColVisibility = useCallback((colId: string): void => {
    setHiddenCols((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      localStorage.setItem('arbi_flagged_columns', JSON.stringify(next));
      if (gridApiRef.current) {
        gridApiRef.current.setColumnsVisible([colId], !next[colId]);
      }
      return next;
    });
  }, []);

  const stateRef = useRef<{
    searchText: string;
    categoryFilter: string;
    statusFilter: StatusFilter;
  }>({ searchText, categoryFilter, statusFilter });
  stateRef.current = { searchText, categoryFilter, statusFilter };

  const getRowsFn = useCallback(async (params: IGetRowsParams): Promise<void> => {
    const { startRow, endRow, sortModel, successCallback, failCallback } = params;
    const { searchText: st, categoryFilter: cf, statusFilter: sf } = stateRef.current;

    try {
      let query = supabase.from('product_flags').select(
        `
          *,
          products!inner (id, isg_sku, title, brand, image_url)
        `,
        { count: 'exact' }
      );

      if (sf !== 'all') query = query.eq('status', sf);
      if (cf !== 'all') query = query.eq('flag_category', cf);

      if (st.trim()) {
        const term = `%${st.trim()}%`;
        query = query.or(
          `isg_sku.ilike.${term},products.title.ilike.${term},products.brand.ilike.${term}`,
          { referencedTable: 'products' }
        );
      }

      if (sortModel.length > 0) {
        const s = sortModel[0];
        query = query.order(s.colId, { ascending: s.sort === 'asc', nullsFirst: false });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      query = query.range(startRow, endRow - 1);
      const { data, count, error } = await query;
      if (error) throw error;

      setTotalCount(count || 0); // KA1-F2 — the exact total drives the toolbar indicator
      successCallback((data ?? []) as unknown as FlagRow[], count || 0);
    } catch (e) {
      reportDatasourceFailure(
        'Flagged',
        { rpcName: 'product_flags', params: { startRow, endRow } },
        e,
        () => failCallback()
      );
    }
  }, []);

  const onGridReady = useCallback(
    (event: GridReadyEvent<FlagRow>): void => {
      gridApiRef.current = event.api;
      event.api.setGridOption('datasource', { getRows: getRowsFn });
      setTimeout(() => {
        const toHide = Object.entries(hiddenCols)
          .filter(([, hidden]) => hidden)
          .map(([colId]) => colId);
        if (toHide.length > 0) event.api.setColumnsVisible(toHide, false);
      }, 0);
    },
    [getRowsFn, hiddenCols]
  );

  const refreshGrid = useCallback((): void => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [getRowsFn]);

  useEffect(() => {
    refreshGrid();
  }, [searchText, categoryFilter, statusFilter, refreshGrid]);

  // ── Actions ───────────────────────────────────────────────

  const handleRescan = useCallback(async (): Promise<void> => {
    setScanning(true);
    const { error } = await supabase.rpc('scan_product_flags');
    setScanning(false);
    if (!error) {
      refreshGrid();
    }
  }, [refreshGrid]);

  const handleResolve = useCallback(
    async (flagId: string): Promise<void> => {
      await supabase
        .from('product_flags')
        .update({
          status: 'resolved',
          resolved_by: user?.id,
          resolved_at: new Date().toISOString(),
        })
        .eq('id', flagId);
      refreshGrid();
    },
    [user, refreshGrid]
  );

  const handleDismiss = useCallback(
    async (flagId: string): Promise<void> => {
      await supabase.from('product_flags').update({ status: 'dismissed' }).eq('id', flagId);
      refreshGrid();
      setDismissConfirm(null);
    },
    [refreshGrid]
  );

  // ── Hold-pile disposition (W4b-β1) — bulk, direct PostgREST on product_flags.
  // archive / named_backlog / chase_upcs written here (admin/user via
  // product_flags_modify_admin_user; viewer/dist_rep are RLS-denied AND the bar is hidden).
  // A non-NULL disposition makes the open flag rescan-immune (scan_product_flags preserves
  // it). 'retry' is NOT offered here — it resets products gate-fields + re-arms the resolver,
  // so it lands as an SD RPC in W4b-β2.
  const onSelectionChanged = useCallback((): void => {
    if (gridApiRef.current) setSelectedRows(gridApiRef.current.getSelectedRows());
  }, []);

  const applyDisposition = useCallback(
    async (value: 'archive' | 'named_backlog' | 'chase_upcs' | null): Promise<void> => {
      const ids = selectedRows.map((r) => r.id);
      if (ids.length === 0 || ids.length > 1000) return;
      setBulkBusy(true);
      const { error } = await supabase
        .from('product_flags')
        .update({ flag_disposition: value })
        .in('id', ids);
      setBulkBusy(false);
      if (error) {
        setToast({ kind: 'error', message: `Disposition failed: ${error.message}` });
        return;
      }
      const n = ids.length;
      setToast({
        kind: 'success',
        message:
          value === null
            ? `Cleared disposition on ${n} flag${n !== 1 ? 's' : ''}`
            : `${n} flag${n !== 1 ? 's' : ''} → ${DISPOSITION_LABELS[value]}`,
      });
      gridApiRef.current?.deselectAll();
      setSelectedRows([]);
      refreshGrid();
    },
    [selectedRows, refreshGrid]
  );

  // W4b-β2 — retry: send the selected flags back into the resolver. Unlike the
  // archive/named_backlog/chase_upcs dispositions (direct PostgREST), retry needs the SD RPC
  // (resets the full W4a review-state set + reconciles the flag + re-arms the import) — direct
  // PostgREST cannot do that atomically. asin-bearing rows are skipped + reported, not reset.
  const applyRetry = useCallback(async (): Promise<void> => {
    const ids = selectedRows.map((r) => r.id);
    if (ids.length === 0 || ids.length > 1000) return;
    setBulkBusy(true);
    const { data, error } = await supabase.rpc('retry_match_flags', { p_flag_ids: ids });
    setBulkBusy(false);
    const env = extractEnvelope<{
      retried_count: number;
      skipped_matched_count: number;
      rearmed_imports: number;
    }>({ data, error });
    if (!env.isOk || !env.data) {
      setToast({ kind: 'error', message: env.userErrors[0]?.message ?? 'Retry failed' });
      return;
    }
    const r = env.data;
    const base = `Retried ${r.retried_count} flag${r.retried_count !== 1 ? 's' : ''}`;
    const skipped =
      r.skipped_matched_count > 0
        ? ` — ${r.skipped_matched_count} already matched (re-pricing is separate)`
        : '';
    setToast({ kind: 'success', message: base + skipped });
    gridApiRef.current?.deselectAll();
    setSelectedRows([]);
    refreshGrid();
  }, [selectedRows, refreshGrid]);

  // ── Cell renderers ────────────────────────────────────────

  type RowParams = ICellRendererParams<FlagRow>;

  const SkuCell = useCallback((p: RowParams) => {
    if (!p.data) return null;
    const sku = p.data.isg_sku || p.data.products?.isg_sku;
    return <span className="font-mono text-accent">{sku}</span>;
  }, []);

  const TitleCell = useCallback((p: RowParams) => {
    if (!p.data?.products) return null;
    return <span className="text-ink-bright font-medium">{p.data.products.title || '—'}</span>;
  }, []);

  const BrandCell = useCallback((p: RowParams) => {
    if (!p.data?.products) return null;
    return <span className="text-ink-secondary">{p.data.products.brand || '—'}</span>;
  }, []);

  const CategoryBadge = useCallback((p: RowParams) => {
    if (!p.data) return null;
    const cat = p.data.flag_category;
    return (
      <span
        className={clsx(
          'px-1.5 py-0.5 rounded text-2xs font-medium',
          (cat && CATEGORY_COLORS[cat]) || 'bg-surface-4 text-ink-muted'
        )}
      >
        {(cat && CATEGORY_LABELS[cat]) || cat || '—'}
      </span>
    );
  }, []);

  const StatusBadge = useCallback((p: RowParams) => {
    if (!p.data) return null;
    return (
      <span
        className={clsx(
          'px-1.5 py-0.5 rounded text-2xs font-medium capitalize',
          STATUS_COLORS[p.data.status] || ''
        )}
      >
        {p.data.status}
      </span>
    );
  }, []);

  const DispositionBadge = useCallback((p: RowParams) => {
    if (!p.data) return null;
    const d = p.data.flag_disposition;
    if (!d) return <span className="text-ink-muted">{'—'}</span>;
    return (
      <span
        className={clsx(
          'px-1.5 py-0.5 rounded text-2xs font-medium',
          DISPOSITION_COLORS[d] || 'bg-surface-4 text-ink-muted'
        )}
      >
        {DISPOSITION_LABELS[d] || d}
      </span>
    );
  }, []);

  const ActionsCell = useCallback(
    (p: RowParams) => {
      if (!p.data || p.data.status !== 'open') return null;
      return (
        <div className="flex items-center gap-1 arbi-row-hover-actions">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleResolve(p.data!.id);
            }}
            className="px-1.5 py-0.5 rounded text-2xs font-medium bg-success/15 text-success hover:bg-success/25 transition-colors cursor-pointer"
          >
            Resolve
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDismissConfirm(p.data!.id);
            }}
            className="px-1.5 py-0.5 rounded text-2xs font-medium bg-surface-4 text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
          >
            Dismiss
          </button>
        </div>
      );
    },
    [handleResolve]
  );

  // ── Column definitions ────────────────────────────────────

  const columnDefs = useMemo<ColDef<FlagRow>[]>(
    () => [
      {
        headerName: 'ISG SKU',
        field: 'isg_sku',
        width: 120,
        pinned: 'left',
        suppressMovable: true,
        resizable: false,
        cellRenderer: SkuCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Title',
        flex: 2,
        minWidth: 200,
        cellRenderer: TitleCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Brand',
        width: 120,
        cellRenderer: BrandCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Category',
        field: 'flag_category',
        width: 120,
        cellRenderer: CategoryBadge,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Reason',
        field: 'flag_reason',
        flex: 1,
        minWidth: 200,
        tooltipField: 'flag_reason', // KA1-F2 (UI \u00a720) \u2014 reveal the full reason on a truncated cell
        valueFormatter: (p: ValueFormatterParams<FlagRow>) =>
          p.value === null || p.value === undefined || p.value === '' ? '\u2014' : String(p.value),
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          color: 'var(--ink-secondary)',
        } as CellStyle,
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 90,
        cellRenderer: StatusBadge,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Disposition',
        field: 'flag_disposition',
        colId: 'flag_disposition',
        width: 110,
        cellRenderer: DispositionBadge,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Actions',
        colId: '_actions',
        width: 150,
        sortable: false,
        filter: false,
        cellRenderer: ActionsCell,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
    ],
    [SkuCell, TitleCell, BrandCell, CategoryBadge, StatusBadge, DispositionBadge, ActionsCell]
  );

  const defaultColDef = useMemo<ColDef<FlagRow>>(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
      valueFormatter: emptyFormatter,
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-2)',
        headerBackgroundColor: 'var(--surface-3)',
        oddRowBackgroundColor: 'var(--surface-2)',
        rowHoverColor: 'var(--surface-3)',
        selectedRowBackgroundColor: 'var(--accent-muted)',
        borderColor: 'var(--border)',
        headerTextColor: 'var(--ink-muted)',
        textColor: 'var(--ink-primary)',
        rowBorder: { color: 'var(--border-subtle)', width: 1, style: 'solid' },
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: '12px',
        headerHeight: 36,
        rowHeight: 42,
        spacing: 4,
      }),
    []
  );

  const onRowClicked = useCallback(
    (event: RowClickedEvent<FlagRow>): void => {
      if (!event.data) return;
      const sku = event.data.isg_sku || event.data.products?.isg_sku;
      if (sku) navigate(`/catalog/flagged/${sku}`);
    },
    [navigate]
  );

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright flex items-center gap-2">
              <Flag size={16} className="text-warning" />
              Flagged Products
            </h1>
            {/* KA1-F2 (FUNCTIONAL Type-1) — muted row-count, thousands-separated (DATA) */}
            {totalCount > 0 && (
              <span className="text-xs text-ink-muted">{totalCount.toLocaleString()} flagged</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Column chooser */}
            <div className="relative">
              <button
                onClick={() => setColChooserOpen((v) => !v)}
                className="arbi-btn-ghost text-xs px-2 py-1"
              >
                <Columns3 size={12} /> Columns
              </button>
              {colChooserOpen && (
                <>
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setColChooserOpen(false)}
                    aria-hidden="true"
                  />
                  <div className="absolute right-0 top-full mt-1 z-50 bg-surface-2 border border-border rounded-lg shadow-xl py-1 w-44">
                    {CHOOSABLE_COLUMNS_FLAGGED.map((col) => (
                      <button
                        key={col.colId}
                        onClick={() => toggleColVisibility(col.colId)}
                        className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-ink-primary hover:bg-surface-3 transition-colors cursor-pointer"
                      >
                        <span
                          className={clsx(
                            'w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0',
                            hiddenCols[col.colId]
                              ? 'border-border bg-surface-3'
                              : 'border-accent bg-accent'
                          )}
                        >
                          {!hiddenCols[col.colId] && <Check size={10} className="text-white" />}
                        </span>
                        {col.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <ExportButton gridApiRef={gridApiRef} fileName="arbi-flagged" />
            <button
              onClick={handleRescan}
              disabled={scanning}
              className="arbi-btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5"
            >
              <RefreshCw size={12} className={scanning ? 'animate-spin' : ''} />
              {scanning ? 'Scanning...' : 'Re-scan Products'}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Category filter — LEFT. W4b-α: render the full set ('All' + the 6 scan_product_flags-emitted
              categories) so every emitted category is reachable — was slice(0,4), which hid
              missing_demand / missing_identifier / no_amazon_listing. text-2xs pills + the status group +
              the w-60 search fit the ~1180 toolbar budget. */}
          <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
            {CATEGORY_OPTIONS.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategoryFilter(c.id)}
                className={clsx(
                  'px-2 py-0.5 text-2xs font-medium rounded transition-colors cursor-pointer',
                  categoryFilter === c.id
                    ? 'bg-surface-4 text-ink-bright'
                    : 'text-ink-muted hover:text-ink-secondary'
                )}
              >
                {c.label.replace('All Categories', 'All')}
              </button>
            ))}
          </div>

          {/* Status filter */}
          <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
            {(['open', 'resolved', 'dismissed', 'all'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={clsx(
                  'px-2 py-0.5 text-2xs font-medium rounded transition-colors cursor-pointer capitalize',
                  statusFilter === s
                    ? 'bg-surface-4 text-ink-bright'
                    : 'text-ink-muted hover:text-ink-secondary'
                )}
              >
                {s}
              </button>
            ))}
          </div>

          {/* Search — RIGHT */}
          <div className="relative ml-auto">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search SKU, title, brand..."
              aria-label="Search flagged products"
              className="arbi-input pl-8 pr-3 py-1 text-xs w-60"
            />
          </div>
        </div>
      </div>

      {/* Bulk disposition bar (W4b-β1) — appears on selection; admin/user only (RLS-backed) */}
      {!isViewer && !isDistRep && selectedRows.length > 0 && (
        <div className="flex-shrink-0 px-4 py-1.5 bg-accent-muted/30 border-b border-accent/30 flex items-center gap-3">
          <span className="text-xs font-medium text-ink-bright">
            {selectedRows.length} selected
          </span>
          <span className="text-2xs uppercase tracking-wide text-ink-muted">Disposition</span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => void applyDisposition('archive')}
              disabled={bulkBusy || selectedRows.length > 1000}
              className="arbi-btn-ghost text-2xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Archive
            </button>
            <button
              onClick={() => void applyDisposition('named_backlog')}
              disabled={bulkBusy || selectedRows.length > 1000}
              className="arbi-btn-ghost text-2xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Named Backlog
            </button>
            <button
              onClick={() => void applyDisposition('chase_upcs')}
              disabled={bulkBusy || selectedRows.length > 1000}
              className="arbi-btn-ghost text-2xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Chase UPCs
            </button>
            <button
              onClick={() => void applyRetry()}
              disabled={bulkBusy || selectedRows.length > 1000}
              className="arbi-btn-ghost text-2xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Retry
            </button>
            <button
              onClick={() => void applyDisposition(null)}
              disabled={bulkBusy}
              className="arbi-btn-ghost text-2xs px-2 py-1 text-ink-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear
            </button>
          </div>
          {selectedRows.length > 1000 && (
            <span className="text-2xs text-danger">
              Exceeds 1000-item cap — narrow the selection.
            </span>
          )}
          <button
            onClick={() => {
              gridApiRef.current?.deselectAll();
              setSelectedRows([]);
            }}
            className="ml-auto text-2xs text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 p-2" style={{ minHeight: 0 }}>
        <AgGridReact<FlagRow>
          ref={gridRef}
          theme={gridTheme}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType="infinite"
          cacheBlockSize={100}
          maxBlocksInCache={20}
          selectionColumnDef={SELECTION_COLUMN_DEF}
          rowSelection={{
            mode: 'multiRow',
            enableClickSelection: false,
            checkboxes: true,
            headerCheckbox: false,
          }}
          onGridReady={onGridReady}
          onRowClicked={onRowClicked}
          onSelectionChanged={onSelectionChanged}
          animateRows={false}
          headerHeight={36}
          rowHeight={42}
          suppressCellFocus={true}
          suppressHorizontalScroll={true}
          overlayNoRowsTemplate='<span style="color:var(--ink-muted);font-size:13px">No flagged products — all products have passed quality checks</span>'
        />
      </div>

      {/* Dismiss confirm */}
      <ConfirmDialog
        open={!!dismissConfirm}
        title="Dismiss Flag"
        message="This will dismiss the flag. The product will not appear in the flagged queue unless re-scanned."
        onConfirm={() => dismissConfirm && handleDismiss(dismissConfirm)}
        onCancel={() => setDismissConfirm(null)}
      />

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
